import { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'shared/helpers/useAppDispatch/useAppDispatch';
import { getIsAuth } from 'pages/login/store/selectors/loginSelectors';
import { loginActions } from 'pages/login/store/store';
import { ErrorWrapper } from 'shared/components/ErrorWrapper';
import { LoaderTriangles } from 'shared/components/loader/LoderLogo';
import { detectAuthEnvironment } from 'shared/helpers/auth/detectAuthEnvironment';
import { appActions } from 'store/appSlice';
import { setAuthUserData } from 'shared/helpers/auth';

type NativeIncomingMessage =
  | { type: 'AUTH_TOKEN'; token: string }
  | { type: 'PING' };

type NativeOutgoingMessage =
  | { type: 'WEB_READY' }
  | { type: 'LOGOUT'; reason?: '401' | 'user' | string }
  | { type: 'BADGE_COUNT'; value: number };

const sendToNative = (msg: NativeOutgoingMessage) => {
  try {
    (window as any).ReactNativeWebView?.postMessage(JSON.stringify(msg));
  } catch (e) {}
};

const listenNativeMessages = (
  handler: (msg: NativeIncomingMessage) => void,
) => {
  const onMessage = (event: MessageEvent) => {
    try {
      const data =
        typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

      if (!data || typeof data !== 'object' || !('type' in data)) return;

      handler(data as NativeIncomingMessage);
    } catch (e) {}
  };

  window.addEventListener('message', onMessage);
  // @ts-ignore (legacy iOS bridge)
  document.addEventListener?.('message', onMessage);

  return () => {
    window.removeEventListener('message', onMessage);
    // @ts-ignore
    document.removeEventListener?.('message', onMessage);
  };
};

export const AuthBootstrap: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const isAuth = useSelector(getIsAuth);

  const [ready, setReady] = useState(false);
  const [fatal, setFatal] = useState('');

  const appliedNativeTokenRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    let unsubscribe: null | (() => void) = null;

    (async () => {
      try {
        const env = detectAuthEnvironment();
        dispatch(appActions.setEnv(env));

        // Native WebView: ждём токен от нативки
        if (env === 'native_webview' && !isAuth) {
          unsubscribe = listenNativeMessages(msg => {
            if (msg.type === 'PING') return;

            if (
              msg.type === 'AUTH_TOKEN' &&
              msg.token &&
              !appliedNativeTokenRef.current
            ) {
              appliedNativeTokenRef.current = true;

              // единый источник истины (как в старой логике)
              setAuthUserData(msg.token);

              // ВАЖНО: тут поставь тот action, который у тебя в проекте реально делает авторизацию.
              // Если у тебя было setToken + setIsAuth — используй их.
              // Пример безопасного варианта:
              // dispatch(loginActions.setToken(msg.token));
              // dispatch(loginActions.setIsAuth(true));

              dispatch(loginActions.setAuth(true));
            }
          });

          sendToNative({ type: 'WEB_READY' });

          // не зависаем на лоадере — даём UI жить, даже если токен не пришёл
          if (mounted) setReady(true);
          return;
        }

        // Telegram (оставляем вашу текущую логику)
        if (env === 'telegram') {
          // ваш telegram auth (initData -> backend -> token)
        }

        // MAX (аналогично Telegram)
        if (env === 'max') {
          // ваш max auth (initData -> backend -> token)
          if (mounted) setReady(true);
          return;
        }

        // Browser (оставляем вашу текущую логику)
        if (env === 'browser') {
          // ваш flow
        }

        if (mounted) setReady(true);
      } catch (e) {
        if (mounted) setFatal('AUTH BOOTSTRAP FAILED');
      }
    })();

    return () => {
      mounted = false;
      unsubscribe?.();
    };
  }, [dispatch, isAuth]);

  if (fatal) return <ErrorWrapper message={fatal} />;
  if (!ready) return <LoaderTriangles />;
  return <>{children}</>;
};

export default AuthBootstrap;
